// src/pages/ContractInsightHistory.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  archiveContractInsightHistory,
  deleteContractInsightHistory,
  getContractInsightDashboard,
  getContractInsightHistoryStats,
  getContractInsightReport,
  listContractInsightHistory,
  renameContractInsightHistory,
  restoreContractInsightHistory,
} from "../utils/contractApi";

const PAGE_SIZE_OPTIONS = [8, 12, 20, 50];

const EMPTY_HISTORY_STATS = Object.freeze({
  total: null,
  ready: null,
  processing: null,
  error: null,
});

// Module-level cache survives normal SPA navigation and browser Back/Forward.
// A full browser refresh naturally clears it and requests fresh summary counts.
const HISTORY_STATS_CACHE_MS = 5 * 60 * 1000;
const historyStatsCache = new Map();
const historyStatsPromises = new Map();

const STATUS_META = {
  complete: {
    label: "Ready",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    dot: "#16A34A",
  },
  completed: {
    label: "Ready",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    dot: "#16A34A",
  },
  report_ready: {
    label: "Ready",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    dot: "#16A34A",
  },
  queued: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  dispatched: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  worker_received: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  ingesting: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  summarizing_files: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  mapping_contract: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  analyzing_case: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  building_report: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  saving_report: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  dispatch_reserved: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  analyzing: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  rendering_pdf: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  finalizing_billing: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  saving_live_view: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  error: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  failed: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  failure: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  billing_error: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  canceled: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  cancelled: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
  abandoned: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
};

function normalizeStatus(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return "processing";
  if (raw === "complete" || raw === "completed" || raw === "report_ready")
    return "complete";
  if (
    raw === "error" ||
    raw === "failed" ||
    raw === "failure" ||
    raw === "billing_error" ||
    raw === "canceled" ||
    raw === "cancelled" ||
    raw === "abandoned"
  ) {
    return "error";
  }
  if (raw === "processing") return "analyzing";
  if (raw === "finalizing") return "saving_live_view";
  if (raw === "dispatched") return "dispatch_reserved";
  return raw;
}

function statusMeta(value) {
  return STATUS_META[normalizeStatus(value)] || STATUS_META.queued;
}

function isReadyStatus(value) {
  return normalizeStatus(value) === "complete";
}

function isProcessingStatus(value) {
  const normalized = normalizeStatus(value);
  return normalized !== "complete" && normalized !== "error";
}

function isErrorStatus(value) {
  return normalizeStatus(value) === "error";
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function pickFirstNumeric(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
  }
  return "";
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function riskLevelFromScore(score, fallbackLevel = "") {
  const numeric = Number(score);
  if (Number.isFinite(numeric)) {
    if (numeric >= 70) return "High";
    if (numeric >= 40) return "Medium";
    return "Low";
  }
  return normalizeRiskLevel(fallbackLevel);
}

function riskColorFromScore(score, fallbackLevel = "") {
  const numeric = Number(score);
  if (Number.isFinite(numeric)) {
    if (numeric >= 70) return "#DC2626";
    if (numeric >= 40) return "#EA580C";
    return "#059669";
  }
  return riskLevelColor(fallbackLevel);
}

function truncateText(value, maxChars = 200) {
  const text = cleanText(value, "");
  if (!text) return "";
  if (text.length <= maxChars) return text;

  const slice = text.slice(0, maxChars + 1);
  const boundary = Math.max(
    slice.lastIndexOf(" "),
    slice.lastIndexOf("."),
    slice.lastIndexOf(","),
    slice.lastIndexOf(";"),
  );

  const cutPoint = boundary >= Math.floor(maxChars * 0.72) ? boundary : maxChars;
  const trimmed = slice
    .slice(0, cutPoint)
    .trim()
    .replace(/[.,;:!?-]+$/g, "");

  return `${trimmed || text.slice(0, maxChars).trim()}...`;
}

function normalizeSummaryCandidate(value) {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "string") {
    return cleanText(value, "");
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return cleanText(value, "");
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (typeof item === "string") return cleanText(item, "");
        if (isPlainObject(item)) {
          return normalizeSummaryCandidate(
            pickFirst(
              item?.dashboardSummary,
              item?.dashboard_summary,
              item?.executiveSummary,
              item?.executive_summary,
              item?.overallSummary,
              item?.overall_summary,
              item?.overallFinding,
              item?.overall_finding,
              item?.summaryText,
              item?.summary_text,
              item?.summary,
              item?.text,
              item?.body,
              item?.content,
              item?.finding,
              item?.description,
            ),
          );
        }
        return cleanText(item, "");
      })
      .filter(Boolean);

    return cleanText(parts.join(" "), "");
  }

  if (isPlainObject(value)) {
    const preferred = pickFirst(
      value?.dashboardSummary,
      value?.dashboard_summary,
      value?.executiveSummary,
      value?.executive_summary,
      value?.overallSummary,
      value?.overall_summary,
      value?.overallFinding,
      value?.overall_finding,
      value?.summaryText,
      value?.summary_text,
      value?.text,
      value?.body,
      value?.content,
      value?.overview,
      value?.conclusion,
      value?.finding,
      value?.description,
      value?.summary,
    );

    if (preferred && preferred !== value) {
      return normalizeSummaryCandidate(preferred);
    }
  }

  return "";
}

function extractSummaryFromSections(sections) {
  if (!sections) return "";

  const sectionMatches = (section) => {
    const label = cleanText(
      pickFirst(section?.title, section?.heading, section?.label, section?.name, section?.sectionTitle),
      "",
    ).toLowerCase();

    return (
      label.includes("dashboard summary") ||
      label.includes("executive summary") ||
      label === "summary" ||
      label.includes("overall finding") ||
      label.includes("overall summary")
    );
  };

  if (Array.isArray(sections)) {
    for (const section of sections) {
      if (!isPlainObject(section) || !sectionMatches(section)) continue;
      const text = normalizeSummaryCandidate(
        pickFirst(
          section?.dashboardSummary,
          section?.dashboard_summary,
          section?.executiveSummary,
          section?.executive_summary,
          section?.overallFinding,
          section?.overall_finding,
          section?.summary,
          section?.text,
          section?.body,
          section?.content,
          section?.paragraphs,
          section?.items,
        ),
      );
      if (text) return text;
    }
  }

  if (isPlainObject(sections)) {
    for (const [key, section] of Object.entries(sections)) {
      const keyText = String(key || "").replace(/[_-]+/g, " ").toLowerCase();
      const keyLooksRight =
        keyText.includes("dashboard summary") ||
        keyText.includes("executive summary") ||
        keyText.includes("overall finding") ||
        keyText.includes("overall summary") ||
        keyText === "summary";

      if (!keyLooksRight && !(isPlainObject(section) && sectionMatches(section))) {
        continue;
      }

      const text = normalizeSummaryCandidate(section);
      if (text) return text;
    }
  }

  return "";
}

function extractSummaryTextFromObject(obj) {
  if (!isPlainObject(obj)) return "";

  const directCandidates = [
    obj?.dashboardSummary,
    obj?.dashboard_summary,
    obj?.executiveSummary,
    obj?.executive_summary,
    obj?.overallSummary,
    obj?.overall_summary,
    obj?.overallFinding,
    obj?.overall_finding,
    obj?.summaryText,
    obj?.summary_text,
    obj?.summary,
    obj?.overview,
    obj?.conclusion,
  ];

  for (const candidate of directCandidates) {
    const text = normalizeSummaryCandidate(candidate);
    if (text) return text;
  }

  const pathCandidates = [
    "meta.dashboardSummary",
    "meta.dashboard_summary",
    "meta.executiveSummary",
    "meta.executive_summary",
    "meta.overallSummary",
    "meta.overall_summary",
    "meta.overallFinding",
    "meta.overall_finding",
    "meta.summaryText",
    "meta.summary_text",
    "meta.summary",
    "metadata.dashboardSummary",
    "metadata.dashboard_summary",
    "metadata.executiveSummary",
    "metadata.executive_summary",
    "metadata.overallFinding",
    "metadata.overall_finding",
    "metadata.summary",
    "dashboard.summary",
    "dashboard.dashboardSummary",
    "dashboard.dashboard_summary",
    "dashboard.executiveSummary",
    "dashboard.executive_summary",
    "summary.overallFinding",
    "summary.overall_finding",
    "summary.executiveSummary",
    "summary.executive_summary",
    "summary.dashboardSummary",
    "summary.dashboard_summary",
    "summary.text",
    "summary.summary",
    "executiveSummary.text",
    "executive_summary.text",
    "report.summary.overallFinding",
    "report.summary.overall_finding",
    "report.summary.executiveSummary",
    "report.summary.executive_summary",
    "report.summary.dashboardSummary",
    "report.summary.dashboard_summary",
    "report.executiveSummary",
    "report.executive_summary",
    "report.dashboardSummary",
    "report.dashboard_summary",
    "finalReport.summary",
    "final_report.summary",
    "finalReport.executiveSummary",
    "final_report.executive_summary",
    "finalReport.dashboardSummary",
    "final_report.dashboard_summary",
    "liveView.dashboardSummary",
    "liveView.dashboard_summary",
    "liveView.executiveSummary",
    "liveView.executive_summary",
    "live_view.dashboardSummary",
    "live_view.dashboard_summary",
    "live_view.executiveSummary",
    "live_view.executive_summary",
  ];

  for (const path of pathCandidates) {
    const text = normalizeSummaryCandidate(getByPath(obj, path));
    if (text) return text;
  }

  return (
    extractSummaryFromSections(obj?.sections) ||
    extractSummaryFromSections(obj?.reportSections) ||
    extractSummaryFromSections(obj?.report_sections) ||
    extractSummaryFromSections(obj?.finalReport?.sections) ||
    extractSummaryFromSections(obj?.final_report?.sections) ||
    ""
  );
}

function normalizeScoreKey(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

function scoreFromPreviewDashboard(source, keys = []) {
  if (!source || typeof source !== "object") return "";

  const directValue = pickFirstNumeric(...keys.map((key) => source?.[key]));
  if (directValue !== "") return directValue;

  const targets = keys.map(normalizeScoreKey).filter(Boolean);
  const items = Array.isArray(source?.items)
    ? source.items
    : Array.isArray(source?.rows)
      ? source.rows
      : Array.isArray(source?.metrics)
        ? source.metrics
        : Array.isArray(source?.data)
          ? source.data
          : [];

  for (const item of items) {
    const label = normalizeScoreKey(
      item?.label || item?.title || item?.name || item?.key || item?.metric,
    );
    if (!label) continue;

    if (targets.some((target) => label.includes(target) || target.includes(label))) {
      const numeric = pickFirstNumeric(
        item?.value,
        item?.value_0_to_100,
        item?.raw_value,
        item?.score_0_to_100,
        item?.score,
        item?.percent,
        item?.rating,
      );
      if (numeric !== "") return numeric;
    }
  }

  return "";
}

function normalizePreviewScore(value) {
  if (value === "" || value === undefined || value === null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? clamp(numeric) : null;
}


function normalizeRiskLevel(value) {
  const text = String(value || "").trim().toLowerCase();
  if (text.includes("high")) return "High";
  if (text.includes("medium") || text.includes("moderate")) return "Medium";
  if (text.includes("low")) return "Low";
  return cleanText(value, "Pending");
}

function riskLevelColor(value) {
  const text = String(value || "").toLowerCase();
  if (text.includes("high")) return "#DC2626";
  if (text.includes("medium") || text.includes("moderate")) return "#EA580C";
  if (text.includes("low")) return "#059669";
  return "#64748B";
}

function getByPath(obj, path, fallback = "") {
  const parts = String(path || "")
    .split(".")
    .filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (cur === undefined || cur === null) return fallback;
    cur = cur?.[part];
  }
  return cur === undefined || cur === null || cur === "" ? fallback : cur;
}

function clamp(n, min = 0, max = 100) {
  return Math.max(min, Math.min(max, Number(n) || 0));
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const date =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "—";
  }
}

function formatShortDate(value) {
  if (!value) return "";
  try {
    const date =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (_) {
    return "";
  }
}

function cleanText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number")
    return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => cleanText(item, ""))
      .filter(Boolean)
      .join(", ");
    return joined || fallback;
  }
  if (typeof value === "object") {
    const preferred =
      value.title || value.label || value.name || value.summary || value.text;
    return preferred ? cleanText(preferred, fallback) : fallback;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (/^\{[\s\S]*\}$/.test(text) || /^\[[\s\S]*\]$/.test(text)) return fallback;
  return text;
}

function formatJobShort(jobId) {
  const text = String(jobId || "").trim();
  if (!text) return "JOB";
  if (text.length <= 18) return text;
  return `${text.slice(0, 14)}…${text.slice(-5)}`;
}

function normalizeHistoryItem(raw = {}) {
  const history =
    raw?.history && typeof raw.history === "object" ? raw.history : raw;

  const artifacts =
    history?.artifacts ||
    history?.artifactPaths ||
    history?.artifact_paths ||
    raw?.artifacts ||
    raw?.artifactPaths ||
    raw?.artifact_paths ||
    {};

  const report =
    history?.report && typeof history.report === "object" ? history.report : {};
  const summaryObj =
    history?.summary && typeof history.summary === "object"
      ? history.summary
      : report?.summary && typeof report.summary === "object"
        ? report.summary
        : {};

  const jobId = pickFirst(
    history?.jobId,
    history?.job_id,
    raw?.jobId,
    raw?.job_id,
    history?.id,
    raw?.id,
  );

  const status = pickFirst(history?.status, raw?.status, "processing");

  const completedAt = pickFirst(
    history?.completedAt,
    history?.completed_at,
    raw?.completedAt,
    raw?.completed_at,
    report?.completedAt,
    report?.completed_at,
  );

  const updatedAt = pickFirst(
    history?.updatedAt,
    history?.updated_at,
    raw?.updatedAt,
    raw?.updated_at,
    completedAt,
  );

  const createdAt = pickFirst(
    history?.createdAt,
    history?.created_at,
    raw?.createdAt,
    raw?.created_at,
    updatedAt,
  );

  const title = pickFirst(
    history?.title,
    history?.jobName,
    history?.job_name,
    history?.contractName,
    history?.contract_name,
    history?.originalName,
    history?.original_name,
    history?.fileName,
    history?.file_name,
    history?.filename,
    raw?.originalName,
    raw?.original_name,
    raw?.fileName,
    raw?.file_name,
    getByPath(history, "contractSnapshot.contractName"),
    getByPath(history, "contract_snapshot.contract_name"),
    getByPath(history, "contractSnapshot.contractTitle"),
    getByPath(history, "contract_snapshot.contract_title"),
    "Contract Insight",
  );

  const riskScore = pickFirst(
    history?.riskScore,
    history?.risk_score,
    summaryObj?.riskScore,
    summaryObj?.risk_score,
    report?.riskScore,
    report?.risk_score,
    getByPath(history, "riskSummary.riskScore"),
    getByPath(history, "risk_summary.risk_score"),
    getByPath(history, "dashboard.riskScore"),
    getByPath(history, "dashboard.risk_score"),
  );

  const riskLevel = pickFirst(
    history?.riskLevel,
    history?.risk_level,
    history?.riskBand,
    history?.risk_band,
    summaryObj?.riskLevel,
    summaryObj?.risk_level,
    report?.riskLevel,
    report?.risk_level,
    getByPath(history, "riskSummary.riskLevel"),
    getByPath(history, "risk_summary.risk_level"),
    getByPath(history, "dashboard.riskLevel"),
    getByPath(history, "dashboard.risk_level"),
    "Pending",
  );

  const pageCount = pickFirst(
    history?.pageCount,
    history?.page_count,
    history?.pagesAnalyzed,
    history?.pages_analyzed,
    history?.pagesTotal,
    history?.pages_total,
    history?.liveViewPageCount,
    history?.live_view_page_count,
    raw?.pageCount,
    raw?.page_count,
    raw?.pagesAnalyzed,
    raw?.pages_analyzed,
    artifacts?.pageCount,
    artifacts?.page_count,
    getByPath(history, "contractPackage.totalPageCount"),
    getByPath(history, "contract_package.total_page_count"),
    getByPath(history, "liveView.pageCount"),
    getByPath(history, "live_view.page_count"),
  );

  const criteriaCount = pickFirst(
    history?.criteriaCount,
    history?.criteria_count,
    history?.canonicalCriteriaCount,
    history?.canonical_criteria_count,
    summaryObj?.criteriaCount,
    summaryObj?.criteria_count,
    getByPath(history, "coverageSummary.total"),
    getByPath(history, "coverage_summary.total"),
  );

  const previewSummary = pickFirst(
    summaryObj?.overallFinding,
    summaryObj?.overall_finding,
    report?.overallFinding,
    report?.overall_finding,
    history?.overallFinding,
    history?.overall_finding,
    history?.summaryText,
    history?.summary_text,
    typeof history?.summary === "string" ? history.summary : "",
    getByPath(history, "dashboard.summary"),
    getByPath(history, "liveView.executiveSummary"),
    getByPath(history, "live_view.executive_summary"),
    "",
  );

  const contractType = pickFirst(
    history?.contractType,
    history?.contract_type,
    history?.schemaId,
    history?.schema_id,
    raw?.contractType,
    raw?.contract_type,
    raw?.schemaId,
    raw?.schema_id,
    getByPath(history, "contractSnapshot.contractType"),
    getByPath(history, "contract_snapshot.contract_type"),
    getByPath(history, "contractProfile.contractType"),
    getByPath(history, "contract_profile.contract_type"),
    "Contract",
  );

  const hasLiveView = Boolean(
    pickFirst(
      history?.hasLiveView,
      history?.has_live_view,
      raw?.hasLiveView,
      raw?.has_live_view,
      history?.liveViewJsonPath,
      history?.live_view_json_path,
      history?.liveViewPath,
      history?.live_view_path,
    ),
  );

  const reportPdfUrl = pickFirst(
    history?.reportPdfUrl,
    history?.report_pdf_url,
    artifacts?.reportPdfUrl,
    artifacts?.report_pdf_url,
    artifacts?.reportPdfSignedUrl,
    artifacts?.report_pdf_signed_url,
    artifacts?.fileVaultUrl,
    artifacts?.file_vault_url,
    raw?.reportPdfUrl,
    raw?.report_pdf_url,
  );

  const reportPdfPath = pickFirst(
    history?.reportPdfPath,
    history?.report_pdf_path,
    artifacts?.reportPdfPath,
    artifacts?.report_pdf_path,
    artifacts?.fileVaultPath,
    artifacts?.file_vault_path,
    raw?.reportPdfPath,
    raw?.report_pdf_path,
  );

  return {
    ...history,
    jobId,
    job_id: jobId,
    status,
    title,
    displayTitle: buildDisplayTitle({ title, jobId, createdAt }),
    completedAt,
    updatedAt,
    createdAt,
    riskScore,
    riskLevel,
    pageCount,
    criteriaCount,
    summary: typeof previewSummary === "string" ? previewSummary : "",
    contractType,
    hasLiveView,
    artifacts,
    reportPdfUrl,
    reportPdfPath,
    fileVaultNodeId: pickFirst(
      history?.fileVaultNodeId,
      history?.file_vault_node_id,
      artifacts?.fileVaultNodeId,
      artifacts?.file_vault_node_id,
    ),
    archived: Boolean(history?.archived || raw?.archived),
    deleted: Boolean(history?.deleted || raw?.deleted),
  };
}

function buildDisplayTitle({ title, jobId, createdAt }) {
  const base = cleanText(title, "");
  const shortDate = formatShortDate(createdAt);
  const generic =
    !base ||
    /^contract analysis$/i.test(base) ||
    /^contract insight report$/i.test(base);
  if (!generic) return base;
  if (shortDate) return `Contract Insight - ${shortDate}`;
  return `Contract Insight - ${formatJobShort(jobId)}`;
}

function normalizeHistoryResponse(data) {
  const rows =
    data?.history ||
    data?.items ||
    data?.contractInsightHistory ||
    data?.contract_insight_history ||
    data?.jobs ||
    data?.case_analyzer_history ||
    data?.results ||
    [];
  return Array.isArray(rows)
    ? rows.map(normalizeHistoryItem).filter((item) => item.jobId)
    : [];
}

function normalizeHistoryStatsResponse(data = {}) {
  const stats =
    data?.stats ||
    data?.summaryCounts ||
    data?.summary_counts ||
    {};

  const optionalCount = (...values) => {
    const value = pickFirstNumeric(...values);
    if (value === "") return null;
    return Math.max(0, Math.trunc(Number(value) || 0));
  };

  return {
    total: optionalCount(
      stats?.total,
      data?.totalAnalyses,
      data?.total_analyses,
      data?.totalCount,
      data?.total_count,
    ),
    ready: optionalCount(
      stats?.ready,
      data?.readyCount,
      data?.ready_count,
    ),
    processing: optionalCount(
      stats?.processing,
      data?.processingCount,
      data?.processing_count,
    ),
    error: optionalCount(
      stats?.error,
      data?.errorCount,
      data?.error_count,
    ),
  };
}

function historyStatsCacheKey(userKey, showAll) {
  return `${String(userKey || "anonymous")}:${showAll ? "all" : "active"}`;
}

function useDebouncedValue(value, delayMs = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
}

function useIsContractHistoryCompact(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;

  const [isCompact, setIsCompact] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsCompact(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isCompact;
}

export default function ContractInsightHistory() {
  const navigate = useNavigate();
  const isCompact = useIsContractHistoryCompact();

  const [idToken, setIdToken] = useState("");
  const [authUserKey, setAuthUserKey] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyStats, setHistoryStats] = useState(EMPTY_HISTORY_STATS);
  const [statsLoading, setStatsLoading] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [pageSize, setPageSize] = useState(8);
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [menuOpenJobId, setMenuOpenJobId] = useState("");
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [menuPos, setMenuPos] = useState(null);

  const refreshInFlightRef = useRef(false);
  const historyLoadedRef = useRef(false);
  const historyRequestSeqRef = useRef(0);
  const statsRequestSeqRef = useRef(0);
  const actionSubmittingRef = useRef(false);
  const menuAnchorRef = useRef(null);

  useEffect(() => {
    setViewMode("list");
    setMenuOpenJobId("");
    setMenuPos(null);
    menuAnchorRef.current = null;
  }, [isCompact]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIdToken("");
        setAuthUserKey("");
        setAuthReady(true);
        setLoading(false);
        setHistoryItems([]);
        setHistoryStats(EMPTY_HISTORY_STATS);
        historyLoadedRef.current = false;
        return;
      }

      try {
        const token = await user.getIdToken();
        setAuthUserKey(user.uid || user.email || "");
        setIdToken(token);
      } catch (err) {
        setError(err?.message || "Unable to verify your login session.");
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadHistoryStats = useCallback(
    async ({
      token = idToken,
      force = false,
      archived = showAll,
    } = {}) => {
      if (!authReady || !token || !authUserKey) return false;

      const cacheKey = historyStatsCacheKey(authUserKey, archived);
      const requestId = ++statsRequestSeqRef.current;

      const cachedEntry = historyStatsCache.get(cacheKey);
      const cacheIsFresh =
        cachedEntry &&
        Date.now() - Number(cachedEntry.fetchedAt || 0) < HISTORY_STATS_CACHE_MS;

      if (!force && cacheIsFresh) {
        setHistoryStats(cachedEntry.data);
        setStatsLoading(false);
        return true;
      }

      setStatsLoading(true);

      let statsPromise =
        !force && historyStatsPromises.has(cacheKey)
          ? historyStatsPromises.get(cacheKey)
          : null;

      if (!statsPromise) {
        statsPromise = getContractInsightHistoryStats({
          idToken: token,
          showAll: archived,
        });
        historyStatsPromises.set(cacheKey, statsPromise);
      }

      try {
        const data = await statsPromise;
        const normalized = normalizeHistoryStatsResponse(data);
        historyStatsCache.set(cacheKey, {
          data: normalized,
          fetchedAt: Date.now(),
        });

        if (requestId === statsRequestSeqRef.current) {
          setHistoryStats(normalized);
        }
        return true;
      } catch (err) {
        // Summary counts are secondary. A stats failure must never block
        // the history rows, pagination, preview, or saved report access.
        if (requestId === statsRequestSeqRef.current) {
          setHistoryStats((current) =>
            current?.total === null ? EMPTY_HISTORY_STATS : current,
          );
        }
        return false;
      } finally {
        if (historyStatsPromises.get(cacheKey) === statsPromise) {
          historyStatsPromises.delete(cacheKey);
        }
        if (requestId === statsRequestSeqRef.current) {
          setStatsLoading(false);
        }
      }
    },
    [authReady, authUserKey, idToken, showAll],
  );

  const refreshHistory = useCallback(
    async ({
      initial = false,
      forceTokenRefresh = false,
      silent = false,
      targetPage = page,
    } = {}) => {
      if (!authReady || !idToken) return false;

      const requestId = ++historyRequestSeqRef.current;
      refreshInFlightRef.current = true;

      if (initial) setLoading(true);
      else setPageLoading(true);
      if (!silent) setRefreshing(true);
      setError("");

      try {
        let token = idToken;
        if (forceTokenRefresh) {
          const currentUser = getAuth().currentUser;
          if (!currentUser)
            throw new Error(
              "Your login session is no longer available. Please sign in again.",
            );
          token = await currentUser.getIdToken(true);
          if (token && token !== idToken) setIdToken(token);
        }

        const data = await listContractInsightHistory({
          idToken: token,
          page: targetPage,
          pageSize,
          query: debouncedQuery,
          status: statusFilter,
          sort: sortKey,
          showAll,
          lookahead: true,
          includeStats: false,
        });

        if (requestId !== historyRequestSeqRef.current) return false;

        const rows = normalizeHistoryResponse(data);
        const pagination = data?.pagination || {};
        const responsePage = Math.max(
          1,
          Number(data?.page || pagination?.page || targetPage || 1),
        );

        setHistoryItems(rows);
        setPage(responsePage);
        setHasPrevious(
          Boolean(
            data?.hasPrevious ??
              data?.has_previous ??
              pagination?.hasPrevious ??
              pagination?.has_previous ??
              responsePage > 1,
          ),
        );
        setHasNext(
          Boolean(
            data?.hasNext ??
              data?.has_next ??
              pagination?.hasNext ??
              pagination?.has_next ??
              false,
          ),
        );
        setSelectedJobId((prev) => {
          if (prev && rows.some((row) => row.jobId === prev)) return prev;
          return rows[0]?.jobId || "";
        });

        if (!rows.length) setSelectedDashboard(null);
        historyLoadedRef.current = true;
        return true;
      } catch (err) {
        if (requestId !== historyRequestSeqRef.current) return false;
        setError(err?.message || "Unable to load Contract Insight history.");
        if (initial) setHistoryItems([]);
        return false;
      } finally {
        if (requestId === historyRequestSeqRef.current) {
          refreshInFlightRef.current = false;
          if (initial) setLoading(false);
          setPageLoading(false);
          if (!silent) setRefreshing(false);
        }
      }
    },
    [
      authReady,
      idToken,
      page,
      pageSize,
      debouncedQuery,
      statusFilter,
      sortKey,
      showAll,
    ],
  );

  useEffect(() => {
    if (!authReady || !idToken) return;
    refreshHistory({
      initial: !historyLoadedRef.current,
      silent: true,
      targetPage: page,
    });
  }, [authReady, idToken, page, refreshHistory]);

  useEffect(() => {
    if (!authReady || !idToken || !authUserKey) return;
    loadHistoryStats({ force: false, archived: showAll });
  }, [authReady, idToken, authUserKey, showAll, loadHistoryStats]);

  useEffect(() => {
    if (!selectedJobId || !idToken) {
      setSelectedDashboard(null);
      setPreviewLoading(false);
      return;
    }

    const selectedHistoryItem = historyItems.find(
      (item) => item.jobId === selectedJobId,
    );
    if (!selectedHistoryItem || !isReadyStatus(selectedHistoryItem.status)) {
      setSelectedDashboard(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;

    async function loadPreview() {
      setPreviewLoading(true);

      try {
        const data = await getContractInsightDashboard(selectedJobId, {
          idToken,
          timeoutMs: 45000,
        });
        if (cancelled) return;
        setSelectedDashboard(data || null);
      } catch (err) {
        if (cancelled) return;
        setSelectedDashboard(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [selectedJobId, idToken, historyItems]);

  const safePage = Math.max(1, page);
  const pageItems = historyItems;
  const visibleStart = pageItems.length
    ? (safePage - 1) * pageSize + 1
    : 0;
  const visibleEnd = pageItems.length
    ? visibleStart + pageItems.length - 1
    : 0;
  const isUnfilteredPage =
    !String(debouncedQuery || "").trim() && statusFilter === "all";

  const selectedItem = useMemo(() => {
    return (
      historyItems.find((item) => item.jobId === selectedJobId) ||
      pageItems[0] ||
      null
    );
  }, [historyItems, pageItems, selectedJobId]);

  const selectedPreview = useMemo(() => {
    const statusPayload =
      selectedDashboard?.statusPayload && typeof selectedDashboard.statusPayload === "object"
        ? selectedDashboard.statusPayload
        : {};

    const job =
      statusPayload?.job ||
      selectedDashboard?.job ||
      selectedDashboard?.contractJob ||
      selectedDashboard?.contract_job ||
      {};

    const report =
      selectedDashboard?.report && typeof selectedDashboard.report === "object"
        ? selectedDashboard.report
        : {};

    const dashboard =
      selectedDashboard?.dashboard ||
      report?.dashboard ||
      {};

    const coverageSummary =
      job?.coverageSummary ||
      job?.coverage_summary ||
      selectedDashboard?.coverageSummary ||
      selectedDashboard?.coverage_summary ||
      report?.coverageSummary ||
      report?.coverage_summary ||
      {};

    const liveViewPayload =
      selectedDashboard?.liveViewPayload ||
      selectedDashboard?.live_view_payload ||
      selectedDashboard?.liveView ||
      selectedDashboard?.live_view ||
      {};

    const liveRaw =
      selectedDashboard?.liveView ||
      selectedDashboard?.live_view ||
      selectedDashboard?.liveViewPayload?.liveView ||
      selectedDashboard?.liveViewPayload?.live_view ||
      selectedDashboard?.liveViewPayload?.thread?.liveView ||
      selectedDashboard?.liveViewPayload?.thread?.live_view ||
      selectedDashboard?.live_view_payload?.liveView ||
      selectedDashboard?.live_view_payload?.live_view ||
      selectedDashboard?.live_view_payload?.thread?.liveView ||
      selectedDashboard?.live_view_payload?.thread?.live_view ||
      {};

    const liveView =
      liveRaw?.liveView ||
      liveRaw?.live_view ||
      liveRaw ||
      {};

    const liveMeta =
      liveViewPayload?.meta ||
      liveViewPayload?.metadata ||
      liveViewPayload?.liveView?.meta ||
      liveViewPayload?.live_view?.meta ||
      liveView?.meta ||
      liveView?.metadata ||
      {};

    const liveRisk =
      liveMeta?.risk ||
      liveViewPayload?.risk ||
      liveViewPayload?.liveView?.risk ||
      liveViewPayload?.live_view?.risk ||
      liveView?.risk ||
      {};

    const riskScore = normalizePreviewScore(
      pickFirstNumeric(
        liveRisk?.riskScore,
        liveRisk?.risk_score,
        selectedItem?.riskScore,
        dashboard?.riskScore,
        dashboard?.risk_score,
        scoreFromPreviewDashboard(dashboard, ["riskScore", "risk_score", "risk score", "overall risk score"]),
        job?.riskScore,
        job?.risk_score,
        report?.riskScore,
        report?.risk_score,
        getByPath(report, "summary.riskScore"),
        getByPath(report, "summary.risk_score"),
        getByPath(job, "report.risk.riskScore"),
        getByPath(job, "report.risk.risk_score"),
      ),
    );

    const riskLevel = riskLevelFromScore(
      riskScore,
      pickFirst(
        liveRisk?.riskLevel,
        liveRisk?.risk_level,
        selectedItem?.riskLevel,
        dashboard?.riskLevel,
        dashboard?.risk_level,
        job?.riskLevel,
        job?.risk_level,
        report?.riskLevel,
        report?.risk_level,
        getByPath(report, "summary.riskLevel"),
        getByPath(report, "summary.risk_level"),
        getByPath(job, "report.risk.riskLevel"),
        getByPath(job, "report.risk.risk_level"),
      ),
    );

    const criteriaTotal = pickFirstNumeric(
      selectedItem?.criteriaCount,
      coverageSummary?.total,
      coverageSummary?.criteriaCount,
      coverageSummary?.criteria_count,
      job?.canonicalCriteriaCount,
      job?.canonical_criteria_count,
      report?.canonicalCriteriaCount,
      report?.canonical_criteria_count,
      report?.criteriaCount,
      report?.criteria_count,
    );

    const criteriaPresent = pickFirstNumeric(
      coverageSummary?.present,
      coverageSummary?.found,
      coverageSummary?.strongEvidence,
      coverageSummary?.strong_evidence,
      report?.coverageSummary?.present,
      report?.coverage_summary?.present,
      report?.coverageSummary?.found,
      report?.coverage_summary?.found,
    );

    const criteriaUnclear = pickFirstNumeric(
      coverageSummary?.unclear,
      coverageSummary?.partial,
      report?.coverageSummary?.unclear,
      report?.coverage_summary?.unclear,
      report?.coverageSummary?.partial,
      report?.coverage_summary?.partial,
    );

    const criteriaNotConfirmed = pickFirstNumeric(
      coverageSummary?.unknown,
      coverageSummary?.notConfirmed,
      coverageSummary?.not_confirmed,
      coverageSummary?.unconfirmed,
      report?.coverageSummary?.unknown,
      report?.coverage_summary?.unknown,
      report?.coverageSummary?.notConfirmed,
      report?.coverage_summary?.not_confirmed,
      report?.coverageSummary?.unconfirmed,
      report?.coverage_summary?.unconfirmed,
    );

    const criteriaNotFound = pickFirstNumeric(
      coverageSummary?.absent,
      coverageSummary?.notFound,
      coverageSummary?.not_found,
      coverageSummary?.missing,
      report?.coverageSummary?.absent,
      report?.coverage_summary?.absent,
      report?.coverageSummary?.notFound,
      report?.coverage_summary?.not_found,
      report?.coverageSummary?.missing,
      report?.coverage_summary?.missing,
    );

    const coverageScore =
      criteriaTotal !== "" && Number(criteriaTotal) > 0 && criteriaPresent !== ""
        ? clamp((Number(criteriaPresent) / Number(criteriaTotal)) * 100)
        : null;

    const reliabilityScore = normalizePreviewScore(
      pickFirstNumeric(
        dashboard?.scoreReliabilityScore,
        dashboard?.score_reliability_score,
        dashboard?.reliabilityScore,
        dashboard?.reliability_score,
        job?.scoreReliabilityScore,
        job?.score_reliability_score,
        report?.scoreReliabilityScore,
        report?.score_reliability_score,
        getByPath(report, "scoreReliability.score"),
        getByPath(report, "score_reliability.score"),
        getByPath(report, "scoreReliability.ratioValidated"),
        getByPath(report, "score_reliability.ratio_validated"),
      ),
    );

    const pages = pickFirstNumeric(
      selectedItem?.pageCount,
      job?.pagesAnalyzed,
      job?.pages_analyzed,
      job?.pagesTotal,
      job?.pages_total,
      job?.liveViewPageCount,
      job?.live_view_page_count,
      liveView?.pageCount,
      liveView?.page_count,
      Array.isArray(liveView?.pages) ? liveView.pages.length : "",
    );

    const paragraphCount = pickFirstNumeric(
      job?.liveViewParagraphCount,
      job?.live_view_paragraph_count,
      liveView?.paragraphCount,
      liveView?.paragraph_count,
      Array.isArray(liveView?.pages)
        ? liveView.pages.reduce(
            (total, pageItem) =>
              total +
              (Array.isArray(pageItem?.paragraphs)
                ? pageItem.paragraphs.length
                : 0),
            0,
          )
        : "",
    );

    const liveViewScore =
      paragraphCount !== "" || selectedItem?.hasLiveView ? 100 : null;

    const summary = pickFirst(
      extractSummaryTextFromObject(liveViewPayload),
      extractSummaryTextFromObject(liveMeta),
      extractSummaryTextFromObject(liveView),
      extractSummaryTextFromObject(report),
      extractSummaryTextFromObject(selectedDashboard),
      selectedItem?.summary,
      report?.summary?.overallFinding,
      report?.summary?.overall_finding,
      report?.overallFinding,
      report?.overall_finding,
      dashboard?.summary,
      liveView?.executiveSummary,
      liveView?.executive_summary,
      "Select a completed contract analysis to preview its risk summary, Live View highlights, and saved report links.",
    );

    const strengths = asTextList(
      liveView?.keyStrengths ||
        liveView?.key_strengths ||
        liveView?.topStrengths ||
        liveView?.top_strengths ||
        report?.keyStrengths ||
        report?.key_strengths,
    );

    const risks = asTextList(
      liveView?.keyRisks ||
        liveView?.key_risks ||
        liveView?.keyWeaknesses ||
        liveView?.key_weaknesses ||
        liveView?.topRisks ||
        liveView?.top_risks ||
        report?.keyRisks ||
        report?.key_risks ||
        report?.missingEvidence ||
        report?.missing_evidence,
    );

    return {
      riskScore,
      riskLevel,
      coverageScore,
      reliabilityScore,
      liveViewScore,
      criteriaTotal: criteriaTotal === "" ? null : Number(criteriaTotal),
      criteriaPresent: criteriaPresent === "" ? null : Number(criteriaPresent),
      coverageFound: criteriaPresent === "" ? null : Number(criteriaPresent),
      coverageUnclear: criteriaUnclear === "" ? null : Number(criteriaUnclear),
      coverageNotConfirmed:
        criteriaNotConfirmed === "" ? null : Number(criteriaNotConfirmed),
      coverageNotFound: criteriaNotFound === "" ? null : Number(criteriaNotFound),
      pages: pages === "" ? null : Number(pages),
      paragraphCount: paragraphCount === "" ? null : Number(paragraphCount),
      summary: truncateText(
        cleanText(
          summary,
          "Summary will appear when the Contract Insight artifact is available.",
        ),
        200,
      ),
      strengths: strengths.length
        ? strengths.slice(0, 3)
        : [
            "Original contract file available",
            "Live View highlights saved",
            "PDF report stored in File Vault",
          ],
      risks: risks.length
        ? risks.slice(0, 3)
        : [
            "Review clauses marked medium or high risk",
            "Check missing or unclear criteria",
            "Open the PDF for full report details",
          ],
    };
  }, [selectedDashboard, selectedItem]);

  const totalCount = historyStats.total;
  const readyCount = historyStats.ready;
  const processingCount = historyStats.processing;
  const errorCount = historyStats.error;

  const openDashboard = (jobId) => {
    if (!jobId) return;
    try {
      sessionStorage.setItem(`contractInsightDashboard:${jobId}`, "allowed");
    } catch (_) {
      // Ignore session storage restrictions.
    }
    navigate(`/contract-insight/dashboard?jobId=${encodeURIComponent(jobId)}`, {
      state: { fromContractInsightHistory: true, jobId },
    });
  };

  const openStatus = (jobId) => {
    if (!jobId) return;
    navigate(`/contract-insight/status?jobId=${encodeURIComponent(jobId)}`, {
      state: { fromContractInsightHistory: true, jobId },
    });
  };

const openPdfReport = async (item) => {
    if (!item?.jobId) return;

    try {
      if (item.reportPdfUrl) {
        window.open(item.reportPdfUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const data = await getContractInsightReport(item.jobId, {
        idToken,
        timeoutMs: 45000,
      });
      const url = pickFirst(
        data?.reportPdfUrl,
        data?.report_pdf_url,
        data?.artifacts?.reportPdfUrl,
        data?.artifacts?.report_pdf_url,
        data?.job?.reportPdfUrl,
        data?.job?.report_pdf_url,
      );

      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      if (item.reportPdfPath) {
        navigate("/file-vault");
        return;
      }

      setError("The PDF report is not available yet. Please open the dashboard or try again in a moment.");
    } catch (err) {
      setError(err?.message || "Unable to open the Contract Insight PDF report.");
    }
  };

  const openHistoryItem = (item) => {
    if (!item?.jobId) return;
    if (isReadyStatus(item.status)) {
      openDashboard(item.jobId);
      return;
    }
    openStatus(item.jobId);
  };

  const reloadHistory = async ({
    forceTokenRefresh = false,
    silent = false,
  } = {}) => {
    return refreshHistory({
      forceTokenRefresh,
      silent,
      targetPage: page,
    });
  };

  const handleManualRefresh = async () => {
    if (refreshInFlightRef.current) return;
    setRefreshing(true);

    // Allow the button to visibly switch to its loading state before token/API work.
    await new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        resolve();
      }
    });

    try {
      await reloadHistory({ forceTokenRefresh: true, silent: true });
      await loadHistoryStats({ force: true, archived: showAll });
    } finally {
      setRefreshing(false);
    }
  };

  const closeHistoryMenu = useCallback(() => {
    setMenuOpenJobId("");
    setMenuPos(null);
    menuAnchorRef.current = null;
  }, []);

  const openHistoryMenu = useCallback((anchor, item) => {
    if (!anchor || !item?.jobId) return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth || 1024;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 768;
    const menuWidth = 176;
    const menuHeight = 132;
    const edge = 10;

    let top = rect.bottom + 8;
    let left = rect.right - menuWidth;

    if (viewportHeight - rect.bottom < menuHeight + edge) {
      top = Math.max(edge, rect.top - menuHeight - 8);
    }

    left = Math.max(edge, Math.min(left, viewportWidth - menuWidth - edge));

    menuAnchorRef.current = anchor;
    setMenuOpenJobId(item.jobId);
    setMenuPos({ top, left });
  }, []);

  const toggleHistoryMenu = useCallback(
    (anchor, item) => {
      if (menuOpenJobId === item?.jobId) {
        closeHistoryMenu();
        return;
      }
      openHistoryMenu(anchor, item);
    },
    [menuOpenJobId, closeHistoryMenu, openHistoryMenu],
  );

  useEffect(() => {
    if (!menuOpenJobId) return undefined;

    const onPointerDown = (event) => {
      const menu = document.getElementById("contract-history-fixed-menu");
      if (
        menu?.contains(event.target) ||
        menuAnchorRef.current?.contains?.(event.target)
      ) {
        return;
      }
      closeHistoryMenu();
    };

    const onViewportChange = () => closeHistoryMenu();
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeHistoryMenu();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange, true);
    };
  }, [menuOpenJobId, closeHistoryMenu]);

  const closeActionModal = (force = false) => {
    if (actionSubmittingRef.current && force !== true) return;
    setActionModal(null);
    setRenameValue("");
  };

  const handleRename = (item) => {
    if (!item?.jobId) return;
    closeHistoryMenu();
    setRenameValue(item.displayTitle || item.title || "");
    setActionModal({ type: "rename", item });
  };

  const handleArchiveToggle = (item) => {
    if (!item?.jobId) return;
    closeHistoryMenu();
    setActionModal({ type: item.archived ? "restore" : "archive", item });
  };

  const handleDelete = (item) => {
    if (!item?.jobId) return;
    closeHistoryMenu();
    setActionModal({ type: "delete", item });
  };

  const confirmActionModal = async () => {
    const item = actionModal?.item;
    const actionType = actionModal?.type;
    if (!item?.jobId || !actionType || actionSubmittingRef.current) return;

    let cleanName = "";
    if (actionType === "rename") {
      cleanName = String(renameValue || "").trim();
      if (!cleanName) {
        setError("Please enter a name for this analysis.");
        return;
      }
    }

    actionSubmittingRef.current = true;
    setActionSubmitting(true);
    setError("");

    // Let React paint the disabled button and spinner before API work begins.
    await new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        window.setTimeout(resolve, 0);
      }
    });

    try {
      if (actionType === "rename") {
        await renameContractInsightHistory(item.jobId, cleanName, { idToken });
      } else if (actionType === "archive") {
        await archiveContractInsightHistory(item.jobId, { idToken });
      } else if (actionType === "restore") {
        await restoreContractInsightHistory(item.jobId, { idToken });
      } else if (actionType === "delete") {
        await deleteContractInsightHistory(item.jobId, { idToken });
      }

      actionSubmittingRef.current = false;
      setActionSubmitting(false);
      closeActionModal(true);
      await reloadHistory();
      if (actionType !== "rename") {
        await loadHistoryStats({ force: true, archived: showAll });
      }
    } catch (err) {
      actionSubmittingRef.current = false;
      setActionSubmitting(false);
      setError(err?.message || "Unable to update this analysis.");
    }
  };

  if (!authReady) {
    return (
      <div className="contract-history-page" style={styles.outerWrap}>
        <div style={styles.loadingCard}>
          <Spinner size={24} />
          <div style={styles.loadingTitle}>
            Preparing Contract Insight history…
          </div>
        </div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div className="contract-history-page" style={styles.outerWrap}>
        <section style={styles.emptyState}>
          <div style={styles.emptyIcon}>◇</div>
          <h1 style={styles.emptyTitle}>Sign in to view Analysis History</h1>
          <p style={styles.emptyText}>
            Your Contract Insight dashboards are saved under your account after
            each contract analysis is completed.
          </p>
          <Link to="/login" style={styles.primaryButton}>
            Sign in
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="contract-history-page" style={styles.outerWrap}>
      <style>{`
        @keyframes contractInsightSpin { to { transform: rotate(360deg); } }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .contract-history-page,
        .contract-history-page *,
        .contract-history-layout,
        .contract-history-layout > *,
        .contract-history-row,
        .contract-history-card,
        .contract-history-actions,
        .contract-history-preview {
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
        }

        .contract-history-page {
          width: 100%;
          overflow-x: hidden;
          contain: inline-size;
        }

        .contract-history-page-buttons {
          max-width: 100%;
          overflow-x: auto;
          padding-bottom: 2px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .contract-history-desktop-filter-row {
          display: flex;
        }

        .contract-history-mobile-utility-row,
        .contract-history-results-toolbar {
          display: none;
        }

        @media (max-width: 1024px) {
          .contract-history-page {
            width: 100% !important;
            max-width: 100% !important;
            padding: 14px 12px 34px !important;
            overflow-x: hidden !important;
          }

          .contract-history-hero {
            display: block !important;
            text-align: center !important;
          }

          .contract-history-hero > div {
            margin-left: auto !important;
            margin-right: auto !important;
          }

          .contract-history-hero .contract-history-new-button {
            margin: 18px auto 0 !important;
            max-width: 100% !important;
            justify-content: center !important;
          }

          .contract-history-title {
            font-size: 28px !important;
            line-height: 1.18 !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .contract-history-subtitle {
            margin-left: auto !important;
            margin-right: auto !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .contract-history-layout {
            grid-template-columns: minmax(0, 1fr) !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .contract-history-preview {
            position: static !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }

          .contract-history-controls {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
            gap: 10px !important;
          }

          .contract-history-search {
            grid-column: 1 / -1 !important;
          }

          .contract-history-desktop-filter-row {
            display: none !important;
          }

          .contract-history-mobile-utility-row {
            display: flex !important;
          }

          .contract-history-stat-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }

          .contract-history-stat-card {
            min-height: 112px !important;
            padding: 15px !important;
            gap: 12px !important;
            align-items: flex-start !important;
          }

          .contract-history-stat-icon {
            width: 44px !important;
            height: 44px !important;
            border-radius: 14px !important;
            font-size: 18px !important;
            flex: 0 0 auto !important;
          }

          .contract-history-results-toolbar {
            display: flex !important;
          }

          .contract-history-desktop-header {
            display: none !important;
          }

          .contract-history-card-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .contract-history-card,
          .contract-history-card * {
            min-width: 0 !important;
            max-width: 100% !important;
          }

          .contract-history-card-title {
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            overflow-wrap: normal !important;
            word-break: normal !important;
          }

          .contract-history-actions {
            width: 100% !important;
            max-width: 100% !important;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto auto !important;
            align-items: center !important;
            gap: 9px !important;
          }

          .contract-history-actions > button:first-child {
            width: 100% !important;
            min-width: 0 !important;
            white-space: normal !important;
          }

          .contract-history-pagination {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 15px 12px !important;
          }

          .contract-history-page-buttons {
            width: 100% !important;
            justify-content: flex-start !important;
          }
        }
      `}</style>

      <header className="contract-history-hero" style={styles.hero}>
        <div>
          <div style={styles.kicker}>Contract Insight</div>
          <h1 className="contract-history-title" style={styles.title}>
            Contract Analysis History
          </h1>
          <p className="contract-history-subtitle" style={styles.subtitle}>
            Review analyzed contracts, reopen live dashboards, and access saved PDF reports.
          </p>
        </div>

        <Link
          to="/contract-insight"
          className="contract-history-new-button"
          style={styles.newButton}
        >
          <span style={styles.newButtonIcon}>＋</span>
          New Analysis
        </Link>
      </header>

      {error ? (
        <div style={styles.errorBox}>
          <strong>Notice:</strong> {error}
        </div>
      ) : null}

      <div
        className="contract-history-layout"
        style={{
          ...styles.layout,
          ...(!isCompact && previewCollapsed
            ? styles.layoutPreviewCollapsed
            : null),
        }}
      >
        <main style={styles.mainColumn}>
          <section style={styles.toolbarPanel}>
            <div className="contract-history-controls" style={styles.controlsGrid}>
              <label className="contract-history-search" style={styles.searchBox}>
                <span style={styles.searchIcon}>⌕</span>
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search contract name, job ID, contract type, or keyword"
                  style={styles.searchInput}
                />
              </label>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                style={styles.select}
              >
                <option value="all">Status: All</option>
                <option value="ready">Ready</option>
                <option value="processing">Processing</option>
                <option value="error">Error</option>
              </select>

              <select
                value={sortKey}
                onChange={(e) => {
                  setPage(1);
                  setSortKey(e.target.value);
                }}
                style={styles.select}
              >
                <option value="newest">Sort: Last updated</option>
                <option value="oldest">Sort: Oldest first</option>
                <option value="score_desc">Sort: Score high to low</option>
                <option value="score_asc">Sort: Score low to high</option>
              </select>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value) || 8);
                }}
                style={styles.select}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="contract-history-desktop-filter-row"
              style={styles.filterRow}
            >
              <FilterButton
                active={statusFilter === "all"}
                onClick={() => {
                  setPage(1);
                  setStatusFilter("all");
                }}
                label="All"
              />
              <FilterButton
                active={statusFilter === "ready"}
                onClick={() => {
                  setPage(1);
                  setStatusFilter("ready");
                }}
                label="Ready"
                dot="#16A34A"
              />
              <FilterButton
                active={statusFilter === "processing"}
                onClick={() => {
                  setPage(1);
                  setStatusFilter("processing");
                }}
                label="Processing"
                dot="#2563EB"
              />
              <FilterButton
                active={statusFilter === "error"}
                onClick={() => {
                  setPage(1);
                  setStatusFilter("error");
                }}
                label="Error"
                dot="#EF4444"
              />

              <span style={styles.filterSpacer} />

              <RefreshButton
                refreshing={refreshing}
                onClick={handleManualRefresh}
              />

              <ShowArchivedToggle
                checked={showAll}
                onChange={(checked) => {
                  setPage(1);
                  setShowAll(checked);
                }}
              />
            </div>

            <div
              className="contract-history-mobile-utility-row"
              style={styles.mobileUtilityRow}
            >
              <ShowArchivedToggle
                checked={showAll}
                onChange={(checked) => {
                  setPage(1);
                  setShowAll(checked);
                }}
              />
              <RefreshButton
                refreshing={refreshing}
                onClick={handleManualRefresh}
              />
            </div>

            <div className="contract-history-stat-grid" style={styles.statGrid}>
              <StatCard
                icon="▣"
                label="Total analyses"
                value={totalCount}
                subtext={statsLoading && totalCount === null ? "Loading summary…" : "All time"}
                color="#2563EB"
              />
              <StatCard
                icon="✓"
                label="Ready"
                value={readyCount}
                subtext={percentText(readyCount, totalCount)}
                color="#059669"
              />
              <StatCard
                icon="◔"
                label="Processing"
                value={processingCount}
                subtext={percentText(processingCount, totalCount)}
                color="#2563EB"
              />
              <StatCard
                icon="!"
                label="Needs review"
                value={errorCount}
                subtext={percentText(errorCount, totalCount)}
                color="#EA580C"
              />
            </div>
          </section>

          <section style={styles.tablePanel}>
            <div
              className="contract-history-results-toolbar"
              style={styles.resultsToolbar}
            >
              <div>
                <div style={styles.resultsEyebrow}>History results</div>
                <div style={styles.resultsCount}>
                  {isUnfilteredPage && totalCount !== null
                    ? `${totalCount.toLocaleString()} analyses`
                    : `${pageItems.length.toLocaleString()} shown on this page`}
                </div>
              </div>

              <ViewModeToggle
                value={viewMode}
                onChange={setViewMode}
              />
            </div>

            {!isCompact ? (
              <div
                className="contract-history-desktop-header"
                style={styles.tableHeader}
              >
                <div>Contract / Job ID</div>
                <div style={styles.centerHeader}>Status</div>
                <div style={styles.centerHeader}>Risk</div>
                <div style={styles.centerHeader}>Pages</div>
                <div style={styles.centerHeader}>Actions</div>
              </div>
            ) : null}

            {loading ? (
              <div style={styles.loadingRows}>
                <Spinner size={22} />
                <span>Loading Analysis History…</span>
              </div>
            ) : pageItems.length ? (
              isCompact && viewMode === "grid" ? (
                <div
                  className="contract-history-card-grid"
                  style={styles.mobileCardGrid}
                >
                  {pageItems.map((item) => (
                    <HistoryCard
                      key={item.jobId}
                      item={item}
                      active={item.jobId === selectedJobId}
                      onSelect={() => setSelectedJobId(item.jobId)}
                      onOpen={() => openHistoryItem(item)}
                      onOpenPdf={() => openPdfReport(item)}
                      onToggleMenu={(anchor) =>
                        toggleHistoryMenu(anchor, item)
                      }
                    />
                  ))}
                </div>
              ) : (
                <div style={styles.rowsWrap}>
                  {pageItems.map((item) => (
                    <HistoryRow
                      key={item.jobId}
                      item={item}
                      isCompact={isCompact}
                      active={item.jobId === selectedJobId}
                      onSelect={() => setSelectedJobId(item.jobId)}
                      onOpen={() => openHistoryItem(item)}
                      onOpenPdf={() => openPdfReport(item)}
                      onToggleMenu={(anchor) =>
                        toggleHistoryMenu(anchor, item)
                      }
                    />
                  ))}
                </div>
              )
            ) : (
              <EmptyHistoryState />
            )}

            <div className="contract-history-pagination" style={styles.pagination}>
              <div style={styles.paginationText}>
                {pageLoading ? (
                  <span style={styles.paginationLoading}>
                    <Spinner size={14} />
                    Loading page…
                  </span>
                ) : (
                  <>
                    Showing {visibleStart} to {visibleEnd}{" "}
                    {isUnfilteredPage && totalCount !== null
                      ? `of ${totalCount.toLocaleString()} analyses`
                      : "matching analyses"}
                  </>
                )}
              </div>

              <div
                className="contract-history-page-buttons"
                style={styles.pageButtons}
              >
                {hasPrevious && safePage > 1 ? (
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pageLoading}
                    style={styles.pageButton}
                    aria-label="Previous history page"
                  >
                    ‹
                  </button>
                ) : null}

                {Array.from({ length: safePage }, (_, index) => index + 1).map(
                  (pageNumber) => {
                    const isCurrentPage = pageNumber === safePage;
                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        disabled={pageLoading || isCurrentPage}
                        style={{
                          ...styles.pageButton,
                          ...(isCurrentPage ? styles.pageButtonActive : null),
                          ...(isCurrentPage ? styles.currentPageButton : null),
                        }}
                        aria-label={
                          isCurrentPage
                            ? `Current page ${pageNumber}`
                            : `Go to history page ${pageNumber}`
                        }
                        aria-current={isCurrentPage ? "page" : undefined}
                      >
                        {pageNumber}
                      </button>
                    );
                  },
                )}

                {hasNext ? (
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={pageLoading}
                    style={styles.pageButton}
                    aria-label="Next history page"
                  >
                    ›
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        </main>

        <aside
          className="contract-history-preview"
          style={
            previewCollapsed
              ? styles.previewCollapsedPanel
              : styles.previewPanel
          }
        >
          {previewCollapsed ? (
            <button
              type="button"
              onClick={() => setPreviewCollapsed(false)}
              style={styles.showPreviewButton}
              title="Show dashboard preview"
            >
              Show Preview
            </button>
          ) : (
            <>
              <div style={styles.previewHeader}>
                <h2 style={styles.previewTitle}>Dashboard Preview</h2>
                <button
                  type="button"
                  onClick={() => setPreviewCollapsed(true)}
                  style={styles.collapsePreviewButton}
                  title="Collapse dashboard preview"
                >
                  Collapse ›
                </button>
              </div>

              {selectedItem ? (
                <>
                  <div style={styles.previewContractTitleRow}>
                    <div style={styles.previewTitleTextWrap}>
                      <h3 style={styles.previewContractTitle}>
                        {selectedItem.displayTitle}
                      </h3>
                      <div style={styles.previewJobId}>
                        {formatJobShort(selectedItem.jobId)}
                      </div>
                    </div>
                    <StatusPill status={selectedItem.status} />
                  </div>

                  <div style={styles.previewUpdated}>
                    Last updated:{" "}
                    {formatDateTime(
                      selectedItem.updatedAt || selectedItem.createdAt,
                    )}
                  </div>

                  <div style={styles.previewDivider} />

                  {isProcessingStatus(selectedItem.status) ? (
                    <HistoryStatusPreview
                      tone="processing"
                      title="Analysis is processing"
                      message="Your contract is being analyzed. Open the live status page to check current progress."
                      actionLabel="Progress"
                      onAction={() => openStatus(selectedItem.jobId)}
                    />
                  ) : isErrorStatus(selectedItem.status) ? (
                    <HistoryStatusPreview
                      tone="error"
                      title="Analysis needs attention"
                      message={cleanText(
                        selectedItem.errorMessage ||
                          getByPath(selectedItem, "error.message"),
                        "The analysis did not complete. Open the status page for details.",
                      )}
                      actionLabel="View Status"
                      onAction={() => openStatus(selectedItem.jobId)}
                    />
                  ) : (
                    <>
                      {previewLoading ? (
                        <div style={styles.previewLoadingState}>
                          <Spinner size={22} />
                          <span>Loading dashboard preview…</span>
                        </div>
                      ) : (
                        <>
                          <h4 style={styles.previewSectionTitle}>Summary</h4>
                          <p style={styles.previewSummary}>
                            {selectedPreview.summary}
                          </p>

                          <div style={styles.previewScoreGrid}>
                            <PreviewScoreCard
                              title="Found"
                              value={selectedPreview.coverageFound}
                              color="#047857"
                              background="#ECFDF5"
                              border="#A7F3D0"
                            />
                            <PreviewScoreCard
                              title="Unclear"
                              value={selectedPreview.coverageUnclear}
                              color="#6D28D9"
                              background="#F5F3FF"
                              border="#DDD6FE"
                            />
                            <PreviewScoreCard
                              title="Not Confirmed"
                              value={selectedPreview.coverageNotConfirmed}
                              color="#B45309"
                              background="#FFFBEB"
                              border="#FDE68A"
                            />
                            <PreviewScoreCard
                              title="Not Found"
                              value={selectedPreview.coverageNotFound}
                              color="#B91C1C"
                              background="#FEF2F2"
                              border="#FECACA"
                            />
                          </div>

                          <h4 style={styles.previewSectionTitle}>
                            Key highlights
                          </h4>
                          <ListPreview
                            items={selectedPreview.strengths}
                            tone="good"
                          />

                          <h4 style={styles.previewSectionTitle}>
                            Watch carefully
                          </h4>
                          <ListPreview
                            items={selectedPreview.risks}
                            tone="risk"
                          />

                          <button
                            type="button"
                            onClick={() => openDashboard(selectedItem.jobId)}
                            style={styles.openFullButton}
                          >
                            Open Full Dashboard ↗
                          </button>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div style={styles.previewEmpty}>
                  <div style={styles.emptyIcon}>▤</div>
                  <h3>No analysis selected</h3>
                  <p>
                    Start a new analysis or select a saved analysis to preview
                    its dashboard.
                  </p>
                  <Link to="/contract-insight" style={styles.primaryButton}>
                    Start New Analysis
                  </Link>
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      <HistoryActionMenu
        item={historyItems.find((row) => row.jobId === menuOpenJobId) || null}
        position={menuPos}
        onRename={() => {
          const item = historyItems.find((row) => row.jobId === menuOpenJobId);
          if (item) handleRename(item);
        }}
        onArchiveToggle={() => {
          const item = historyItems.find((row) => row.jobId === menuOpenJobId);
          if (item) handleArchiveToggle(item);
        }}
        onDelete={() => {
          const item = historyItems.find((row) => row.jobId === menuOpenJobId);
          if (item) handleDelete(item);
        }}
      />

      <ActionModal
        actionModal={actionModal}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        submitting={actionSubmitting}
        onCancel={closeActionModal}
        onConfirm={confirmActionModal}
      />
    </div>
  );
}

function percentText(value, total) {
  if (!total) return "0%";
  return `${Math.round((Number(value || 0) / Number(total || 1)) * 100)}%`;
}

function asTextList(value) {
  const rows = Array.isArray(value) ? value : value ? [value] : [];
  return rows
    .map((item) => {
      if (typeof item === "string") return cleanText(item, "");
      if (item && typeof item === "object") {
        return cleanText(
          item.text ||
            item.summary ||
            item.title ||
            item.label ||
            item.risk ||
            item.strength ||
            item.item ||
            item.finding,
          "",
        );
      }
      return cleanText(item, "");
    })
    .filter(Boolean);
}

function FilterButton({ active, label, dot, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.filterButton,
        ...(active ? styles.filterButtonActive : null),
      }}
    >
      {dot ? <span style={{ ...styles.filterDot, background: dot }} /> : null}
      {label}
    </button>
  );
}

function StatCard({ icon, label, value, subtext, color }) {
  return (
    <div className="contract-history-stat-card" style={styles.statCard}>
      <div
        className="contract-history-stat-icon"
        style={{ ...styles.statIcon, background: `${color}12`, color }}
      >
        {icon}
      </div>
      <div>
        <div style={styles.statLabel}>{label}</div>
        <div style={styles.statValue}>
          {Number(value || 0).toLocaleString()}
        </div>
        <div style={styles.statSubtext}>{subtext}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = statusMeta(status);
  return (
    <span
      style={{
        ...styles.statusPill,
        color: meta.color,
        background: meta.bg,
        borderColor: meta.border,
      }}
    >
      <span style={{ ...styles.statusDot, background: meta.dot }} />
      {meta.label}
    </span>
  );
}

function getHistoryActionDetails(item) {
  const ready = isReadyStatus(item.status);
  const processing = isProcessingStatus(item.status);
  const error = isErrorStatus(item.status);
  const score =
    ready &&
    item.riskScore !== "" &&
    item.riskScore !== undefined &&
    item.riskScore !== null
      ? `${Math.round(clamp(item.riskScore))}%`
      : "—";

  return {
    ready,
    processing,
    error,
    score,
    contractText: `${cleanText(item.pageCount, "—")} pages`,
    primaryLabel: processing
      ? "Progress"
      : error
        ? "View Status"
        : "View",
    primaryTitle: processing
      ? "Open live contract analysis progress"
      : error
        ? "Open contract analysis status and error details"
        : "View completed Contract Insight dashboard",
  };
}

function HistoryActions({ item, onOpen, onOpenPdf, onToggleMenu }) {
  const details = getHistoryActionDetails(item);

  return (
    <div className="contract-history-actions" style={styles.rowActions}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        style={{
          ...styles.openButton,
          ...(details.processing ? styles.progressButton : null),
          ...(details.error ? styles.errorStatusButton : null),
        }}
        title={details.primaryTitle}
      >
        {details.primaryLabel}
      </button>

      {details.ready ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenPdf?.();
          }}
          style={styles.pdfButton}
          title="Open full PDF report"
        >
          PDF
        </button>
      ) : null}

      <MoreActionsButton onToggleMenu={onToggleMenu} />
    </div>
  );
}

function HistoryRow({
  item,
  isCompact,
  active,
  onSelect,
  onOpen,
  onOpenPdf,
  onToggleMenu,
}) {
  const meta = statusMeta(item.status);
  const details = getHistoryActionDetails(item);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className="contract-history-row"
      style={{
        ...(isCompact ? styles.mobileRowButton : styles.rowButton),
        ...(active ? styles.rowButtonActive : null),
      }}
    >
      {isCompact ? (
        <>
          <div style={styles.mobileListTopRow}>
            <div
              style={{
                ...styles.fileIcon,
                background: `${meta.color}10`,
                color: meta.color,
              }}
            >
              ▧
            </div>

            <div style={styles.mobileListTitleWrap}>
              <div style={styles.caseTitle}>
                <span
                  style={styles.caseTitleText}
                  title={item.displayTitle}
                >
                  {item.displayTitle}
                </span>
                {item.archived ? (
                  <span style={styles.archivedInline}>Archived</span>
                ) : null}
              </div>
            </div>

            <div style={styles.mobileListStatus}>
              <StatusPill status={item.status} />
            </div>
          </div>

          <div style={styles.mobileMetaLine}>
            <span style={styles.mobileMetaPill}>Risk {details.score}</span>
            <span style={styles.mobileMetaPill}>▱ {details.contractText}</span>
          </div>

          <div style={styles.mobileListActions}>
            <HistoryActions
              item={item}
              onOpen={onOpen}
              onOpenPdf={onOpenPdf}
              onToggleMenu={onToggleMenu}
            />
          </div>
        </>
      ) : (
        <>
          <div style={styles.caseCell}>
            <div
              style={{
                ...styles.fileIcon,
                background: `${meta.color}10`,
                color: meta.color,
              }}
            >
              ▧
            </div>
            <div style={styles.caseTextBlock}>
              <div style={styles.caseTitle}>
                <span
                  style={styles.caseTitleText}
                  title={item.displayTitle}
                >
                  {item.displayTitle}
                </span>
                {item.archived ? (
                  <span style={styles.archivedInline}>Archived</span>
                ) : null}
              </div>
            </div>
          </div>

          <div style={styles.centerCell}>
            <StatusPill status={item.status} />
          </div>
          <div style={styles.centerCell}>
            <span
              style={{
                ...styles.scoreBadge,
                ...(!details.ready ? styles.pendingScoreBadge : null),
              }}
            >
              {details.score}
            </span>
          </div>
          <div style={styles.centerCell}>
            <span style={styles.fileCount}>▱ {details.contractText}</span>
          </div>
          <HistoryActions
            item={item}
            onOpen={onOpen}
            onOpenPdf={onOpenPdf}
            onToggleMenu={onToggleMenu}
          />
        </>
      )}
    </div>
  );
}

function HistoryCard({
  item,
  active,
  onSelect,
  onOpen,
  onOpenPdf,
  onToggleMenu,
}) {
  const meta = statusMeta(item.status);
  const details = getHistoryActionDetails(item);

  return (
    <article
      className="contract-history-card"
      style={{
        ...styles.mobileCard,
        ...(active ? styles.mobileCardActive : null),
      }}
      onClick={onSelect}
    >
      <div style={styles.mobileCardHeader}>
        <div
          style={{
            ...styles.mobileCardIcon,
            background: `${meta.color}10`,
            color: meta.color,
          }}
        >
          ▧
        </div>

        <div style={styles.mobileCardTitleWrap}>
          <h3
            className="contract-history-card-title"
            style={styles.mobileCardTitle}
            title={item.displayTitle}
          >
            {item.displayTitle}
          </h3>
        </div>

        <div style={styles.mobileCardStatusGroup}>
          <StatusPill status={item.status} />
          {item.archived ? (
            <span style={styles.archivedCardBadge}>Archived</span>
          ) : null}
        </div>
      </div>

      <div style={styles.mobileCardMetrics}>
        <div style={styles.mobileMetric}>
          <span style={styles.mobileMetricLabel}>Risk</span>
          <strong style={styles.mobileMetricValue}>{details.score}</strong>
        </div>
        <div style={styles.mobileMetric}>
          <span style={styles.mobileMetricLabel}>Pages</span>
          <strong style={styles.mobileMetricValue}>
            {cleanText(item.pageCount, "—")}
          </strong>
        </div>
      </div>

      <HistoryActions
        item={item}
        onOpen={onOpen}
        onOpenPdf={onOpenPdf}
        onToggleMenu={onToggleMenu}
      />
    </article>
  );
}

function MoreActionsButton({ onToggleMenu }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggleMenu(event.currentTarget);
      }}
      style={styles.moreButton}
      aria-label="More actions"
      title="Rename, archive, or delete"
    >
      ⋮
    </button>
  );
}

function HistoryActionMenu({
  item,
  position,
  onRename,
  onArchiveToggle,
  onDelete,
}) {
  if (!item || !position) return null;

  return (
    <div
      id="contract-history-fixed-menu"
      style={{
        ...styles.actionMenu,
        top: position.top,
        left: position.left,
      }}
      role="menu"
    >
      <button type="button" style={styles.actionMenuItem} onClick={onRename}>
        Rename
      </button>
      <button
        type="button"
        style={styles.actionMenuItem}
        onClick={onArchiveToggle}
      >
        {item.archived ? "Restore" : "Archive"}
      </button>
      <button
        type="button"
        style={{ ...styles.actionMenuItem, ...styles.actionMenuItemDanger }}
        onClick={onDelete}
      >
        Delete
      </button>
    </div>
  );
}

function RefreshButton({ refreshing, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={refreshing}
      style={{
        ...styles.refreshButton,
        ...(refreshing ? styles.refreshButtonDisabled : null),
      }}
      title="Refresh Analysis History"
      aria-label="Refresh Analysis History"
    >
      {refreshing ? (
        <Spinner size={15} color="#2563EB" />
      ) : (
        <span style={styles.refreshIcon}>↻</span>
      )}
      <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
    </button>
  );
}

function ShowArchivedToggle({ checked, onChange }) {
  return (
    <label style={styles.showAllToggle}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        style={styles.showAllCheckbox}
      />
      <span>Show archived</span>
    </label>
  );
}

function ViewModeToggle({ value, onChange }) {
  return (
    <div style={styles.viewToggle} aria-label="History view">
      <button
        type="button"
        onClick={() => onChange("list")}
        style={{
          ...styles.viewToggleButton,
          ...(value === "list" ? styles.viewToggleButtonActive : null),
        }}
        aria-pressed={value === "list"}
      >
        List
      </button>
      <button
        type="button"
        onClick={() => onChange("grid")}
        style={{
          ...styles.viewToggleButton,
          ...(value === "grid" ? styles.viewToggleButtonActive : null),
        }}
        aria-pressed={value === "grid"}
      >
        Grid
      </button>
    </div>
  );
}

function ActionModal({
  actionModal,
  renameValue,
  setRenameValue,
  submitting,
  onCancel,
  onConfirm,
}) {
  if (!actionModal) return null;

  const item = actionModal.item || {};
  const title =
    actionModal.type === "rename"
      ? "Rename Analysis"
      : actionModal.type === "delete"
        ? "Remove Analysis?"
        : actionModal.type === "restore"
          ? "Restore Analysis?"
          : "Archive Analysis?";

  const description =
    actionModal.type === "rename"
      ? "Enter a clear name so this analysis is easier to find later."
      : actionModal.type === "delete"
        ? "This removes the item from Analysis History. It will not delete the File Vault PDF or completed report files."
        : actionModal.type === "restore"
          ? "This will return the analysis to your default history list."
          : "This hides the analysis from the default list. You can bring it back using Show archived.";

  const confirmLabel =
    actionModal.type === "rename"
      ? "Save Name"
      : actionModal.type === "delete"
        ? "Remove"
        : actionModal.type === "restore"
          ? "Restore"
          : "Archive";

  const submittingLabel =
    actionModal.type === "rename"
      ? "Saving…"
      : actionModal.type === "delete"
        ? "Removing…"
        : actionModal.type === "restore"
          ? "Restoring…"
          : "Archiving…";

  return (
    <div
      style={styles.modalOverlay}
      onMouseDown={submitting ? undefined : onCancel}
      aria-busy={submitting ? "true" : "false"}
    >
      <div style={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <h3 style={styles.modalTitle}>{title}</h3>
            <p style={styles.modalSubtitle}>{description}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              ...styles.modalCloseButton,
              ...(submitting ? styles.modalDisabledButton : null),
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.modalItemName}>
            {cleanText(item.displayTitle || item.title, "Selected analysis")}
          </div>

          {actionModal.type === "rename" ? (
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Enter analysis name"
              style={styles.modalInput}
              disabled={submitting}
              autoFocus
            />
          ) : null}
        </div>

        <div style={styles.modalActions}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              ...styles.modalCancelButton,
              ...(submitting ? styles.modalDisabledButton : null),
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            style={{
              ...styles.modalConfirmButton,
              ...(actionModal.type === "delete"
                ? styles.modalDangerButton
                : null),
              ...(submitting ? styles.modalSubmittingButton : null),
            }}
          >
            {submitting ? <Spinner size={15} color="#FFFFFF" /> : null}
            <span>{submitting ? submittingLabel : confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryStatusPreview({ tone, title, message, actionLabel, onAction }) {
  const isError = tone === "error";
  return (
    <div
      style={{
        ...styles.statusPreviewCard,
        ...(isError ? styles.statusPreviewCardError : null),
      }}
    >
      <div
        style={{
          ...styles.statusPreviewIcon,
          ...(isError ? styles.statusPreviewIconError : null),
        }}
      >
        {isError ? "!" : <Spinner size={22} />}
      </div>
      <h4 style={styles.statusPreviewTitle}>{title}</h4>
      <p style={styles.statusPreviewText}>{message}</p>
      <button
        type="button"
        onClick={onAction}
        style={{
          ...styles.openFullButton,
          ...(isError ? styles.openFullButtonError : null),
        }}
      >
        {actionLabel} ↗
      </button>
    </div>
  );
}

function PreviewScoreCard({
  title,
  value,
  color = "#2563EB",
  background = "#FFFFFF",
  border = "rgba(148,163,184,0.22)",
}) {
  const hasValue = typeof value === "number" && Number.isFinite(value);
  const displayValue = hasValue ? Math.max(0, Math.trunc(value)) : "—";

  return (
    <div
      style={{
        ...styles.previewScoreCard,
        background,
        borderColor: border,
      }}
    >
      <div style={{ ...styles.previewScoreTitle, color }}>{title}</div>
      <div style={{ ...styles.previewCoverageValue, color }}>{displayValue}</div>
    </div>
  );
}

function ListPreview({ items, tone }) {
  const icon = tone === "risk" ? "△" : "✓";
  const color = tone === "risk" ? "#EF4444" : "#059669";

  return (
    <div style={styles.listPreview}>
      {(items || []).slice(0, 3).map((item, idx) => (
        <div key={`${item}-${idx}`} style={styles.previewListRow}>
          <span style={{ ...styles.previewListIcon, color }}>{icon}</span>
          <span>{cleanText(item)}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyHistoryState() {
  return (
    <div style={styles.emptyInline}>
      <div style={styles.emptyIcon}>▤</div>
      <h2 style={styles.emptyInlineTitle}>No contract analyses yet</h2>
      <p style={styles.emptyInlineText}>
        Start a new contract analysis to generate your first Contract Insight dashboard.
        Completed dashboards will appear here automatically.
      </p>
      <Link to="/contract-insight" style={styles.primaryButton}>
        Start New Analysis
      </Link>
    </div>
  );
}

function Spinner({ size = 18, color = "#2563EB" }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `2px solid ${color}22`,
        borderTopColor: color,
        borderRadius: "999px",
        display: "inline-block",
        animation: "contractInsightSpin .8s linear infinite",
      }}
    />
  );
}

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    minHeight: "100vh",
    margin: "0 auto",
    overflowX: "hidden",
    background:
    "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
  },
  hero: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
    padding: "12px 0 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
  },
  kicker: {
    color: "#2563EB",
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    fontSize: 12,
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.035em",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 640,
  },
  newButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 18px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    textDecoration: "none",
    fontWeight: 900,
    boxShadow: "0 14px 28px rgba(37,99,235,0.24)",
    whiteSpace: "nowrap",
  },
  newButtonIcon: {
    fontSize: 17,
    fontWeight: 900,
  },
  layout: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 320px)",
    gap: 18,
    alignItems: "start",
  },
  layoutPreviewCollapsed: {
    gridTemplateColumns: "minmax(0, 1fr) 124px",
  },
  mainColumn: {
    minWidth: 0,
  },
  toolbarPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
  },
  controlsGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1fr) 170px 190px 130px",
    gap: 12,
    alignItems: "center",
  },
  searchBox: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 46,
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.28)",
    borderRadius: 13,
    padding: "0 14px",
    boxShadow: "0 8px 18px rgba(15,23,42,0.03)",
  },
  searchIcon: {
    color: "#64748B",
    fontSize: 20,
  },
  searchInput: {
    minWidth: 0,
    maxWidth: "100%",
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: 14,
    color: "#0F172A",
    background: "transparent",
  },
  select: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 46,
    borderRadius: 13,
    border: "1px solid rgba(148,163,184,0.28)",
    padding: "0 12px",
    background: "#FFFFFF",
    color: "#0F172A",
    fontWeight: 750,
    outline: "none",
  },
  filterRow: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    gap: 10,
    marginTop: 14,
    flexWrap: "wrap",
  },
  filterButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 11,
    border: "1px solid rgba(148,163,184,0.28)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 850,
    cursor: "pointer",
  },
  filterButtonActive: {
    background: "#2563EB",
    color: "#FFFFFF",
    borderColor: "#2563EB",
    boxShadow: "0 10px 22px rgba(37,99,235,0.20)",
  },
  filterDot: {
    width: 8,
    height: 8,
    borderRadius: "999px",
  },
  statGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginTop: 16,
  },
  statCard: {
    minWidth: 0,
    overflow: "hidden",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.20)",
    borderRadius: 18,
    padding: 18,
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 12px 26px rgba(15,23,42,0.05)",
  },
  statIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    fontSize: 21,
  },
  statLabel: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  statValue: {
    color: "#0F172A",
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: "-0.03em",
    marginTop: 2,
  },
  statSubtext: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 1,
  },
  tablePanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    marginTop: 18,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 0,
    overflow: "visible",
    isolation: "isolate",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
  },
  tableHeader: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(160px, 1.55fr) 86px 80px 92px minmax(150px, .95fr)",
    gap: 12,
    padding: "14px 18px",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 950,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  centerHeader: {
    textAlign: "center",
  },
  mobileListHeader: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 116px",
    gap: 10,
    padding: "13px 18px",
    color: "#64748B",
    fontSize: 13,
    fontWeight: 950,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  rowsWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gap: 0,
  },
  rowButton: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "minmax(160px, 1.55fr) 86px 80px 92px minmax(150px, .95fr)",
    gap: 12,
    alignItems: "center",
    textAlign: "left",
    border: "0",
    borderBottom: "1px solid rgba(148,163,184,0.16)",
    background: "#FFFFFF",
    padding: "15px 18px",
    cursor: "pointer",
    color: "#0F172A",
  },
  rowButtonActive: {
    background: "#F8FBFF",
    boxShadow: "inset 3px 0 0 #2563EB",
  },
  mobileRowButton: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "block",
    textAlign: "left",
    border: "0",
    borderBottom: "1px solid rgba(148,163,184,0.16)",
    background: "#FFFFFF",
    padding: "16px 18px",
    cursor: "pointer",
    color: "#0F172A",
  },
  mobileListTopRow: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "38px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 13,
  },
  mobileListTitleWrap: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
  },
  mobileListStatus: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  caseCell: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    minWidth: 0,
  },
  fileIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    fontWeight: 950,
  },
  caseTextBlock: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  caseTitle: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 950,
    color: "#0F172A",
    whiteSpace: "nowrap",
  },
  caseTitleText: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  jobLine: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    marginTop: 3,
  },
  rowMetaGrid: {
    display: "grid",
    gridTemplateColumns: "110px 110px 110px 1fr",
    alignItems: "center",
    gap: 12,
  },
  centerCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    textAlign: "center",
  },
  mobileStatusCell: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    minWidth: 0,
    textAlign: "center",
  },
  mobileMetaLine: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    marginTop: 10,
    paddingLeft: 51,
  },
  mobileMetaPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 9px",
    background: "#F8FAFC",
    border: "1px solid rgba(148,163,184,0.22)",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: "6px 10px",
    border: "1px solid",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 950,
    width: "fit-content",
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "999px",
  },
  scoreBadge: {
    display: "inline-flex",
    justifyContent: "center",
    width: "fit-content",
    color: "#047857",
    background: "#ECFDF5",
    border: "1px solid #BBF7D0",
    padding: "6px 10px",
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 950,
  },
  fileCount: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  updatedText: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 750,
  },
  rowActions: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  mobileRowActions: {
    width: "100%",
    maxWidth: "100%",
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    minWidth: 0,
    paddingTop: 8,
  },
  openButton: {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    textAlign: "center",
    border: "1px solid rgba(37,99,235,0.38)",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 950,
    cursor: "pointer",
    minWidth: 52,
  },
  progressButton: {
    background: "#EFF6FF",
    borderColor: "#93C5FD",
    color: "#1D4ED8",
    minWidth: 112,
  },
  errorStatusButton: {
    background: "#FEF2F2",
    borderColor: "#FCA5A5",
    color: "#B91C1C",
    minWidth: 96,
  },
  pendingScoreBadge: {
    color: "#64748B",
    background: "#F8FAFC",
    borderColor: "#E2E8F0",
  },
  pdfButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    border: "1px solid rgba(148,163,184,0.30)",
    background: "#FFFFFF",
    color: "#0F172A",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 900,
    textDecoration: "none",
    fontSize: 13,
    minWidth: 44,
    textAlign: "center",
  },

  filterSpacer: {
    flex: "1 1 auto",
    minWidth: 12,
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 11,
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1D4ED8",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  refreshButtonDisabled: {
    cursor: "wait",
    opacity: 0.72,
  },
  refreshIcon: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 900,
  },
  showAllToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 11,
    border: "1px solid rgba(148,163,184,0.28)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 850,
    cursor: "pointer",
  },
  showAllCheckbox: {
    width: 15,
    height: 15,
    accentColor: "#2563EB",
  },
  archivedInline: {
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "2px 7px",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 10,
    fontWeight: 950,
    verticalAlign: "middle",
  },
  menuWrapper: {
    position: "relative",
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
  },
  moreButton: {
    width: 42,
    height: 42,
    flex: "0 0 42px",
    border: "1px solid rgba(148,163,184,0.28)",
    borderRadius: 11,
    background: "#FFFFFF",
    color: "#475569",
    fontWeight: 950,
    fontSize: 21,
    padding: 0,
    cursor: "pointer",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionMenu: {
    position: "fixed",
    width: 176,
    maxWidth: "calc(100vw - 20px)",
    background: "#FFFFFF",
    color: "#0F172A",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 12,
    boxShadow: "0 18px 38px rgba(15,23,42,0.18)",
    zIndex: 1200,
    overflow: "hidden",
  },
  actionMenuItem: {
    display: "block",
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    color: "#0F172A",
    textAlign: "left",
    padding: "10px 12px",
    fontWeight: 850,
    cursor: "pointer",
  },
  actionMenuItemDanger: {
    color: "#DC2626",
  },

  moreDots: {
    color: "#64748B",
    fontWeight: 950,
    fontSize: 18,
    padding: "0 2px",
  },
  processingMini: {
    width: 140,
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
  },
  processingTrack: {
    marginTop: 6,
    height: 7,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  processingFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #2563EB, #1D4ED8)",
  },
  pagination: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    background: "#FFFFFF",
  },
  paginationText: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  paginationLoading: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  pageButtons: {
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  pageButton: {
    width: 34,
    height: 34,
    borderRadius: 9,
    border: "1px solid rgba(148,163,184,0.30)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
  },
  pageButtonActive: {
    background: "#EFF6FF",
    borderColor: "#2563EB",
    color: "#2563EB",
  },
  currentPageButton: {
    cursor: "default",
    opacity: 1,
  },
  pageEllipsis: {
    color: "#64748B",
    padding: "0 4px",
    fontWeight: 950,
  },
  mobileUtilityRow: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    marginTop: 14,
    display: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  resultsToolbar: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    padding: "14px 16px",
    display: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  resultsEyebrow: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  resultsCount: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    marginTop: 2,
  },
  viewToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: 4,
    borderRadius: 11,
    background: "#E2E8F0",
    flex: "0 0 auto",
  },
  viewToggleButton: {
    minHeight: 34,
    padding: "0 11px",
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  viewToggleButtonActive: {
    background: "#FFFFFF",
    color: "#1D4ED8",
    boxShadow: "0 4px 12px rgba(15,23,42,0.10)",
  },
  mobileCardGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: 12,
    padding: 12,
    background: "#F8FAFC",
  },
  mobileCard: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
  },
  mobileCardActive: {
    borderColor: "#93C5FD",
    boxShadow: "0 0 0 2px #DBEAFE, 0 12px 26px rgba(37,99,235,0.10)",
  },
  mobileCardHeader: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 11,
  },
  mobileCardIdentity: {
    display: "flex",
    alignItems: "flex-start",
    gap: 11,
    minWidth: 0,
    flex: "1 1 auto",
  },
  mobileCardIcon: {
    width: 42,
    height: 42,
    flex: "0 0 42px",
    borderRadius: 13,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
  },
  mobileCardTitleWrap: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    flex: "1 1 auto",
  },
  mobileCardTitle: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    margin: 0,
    color: "#0F172A",
    fontSize: 15,
    lineHeight: 1.35,
    fontWeight: 950,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflowWrap: "normal",
    wordBreak: "normal",
  },
  mobileCardJob: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    marginTop: 4,
    overflowWrap: "anywhere",
  },
  mobileCardStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  mobileCardStatusGroup: {
    minWidth: 0,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    flexWrap: "nowrap",
  },
  archivedCardBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    borderRadius: 999,
    padding: "0 10px",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 11,
    fontWeight: 900,
  },
  mobileCardMetrics: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 14,
    marginBottom: 14,
  },
  mobileMetric: {
    minWidth: 0,
    borderRadius: 13,
    background: "#F8FAFC",
    border: "1px solid rgba(148,163,184,0.18)",
    padding: "11px 12px",
  },
  mobileMetricLabel: {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
  },
  mobileMetricValue: {
    display: "block",
    color: "#0F172A",
    fontSize: 17,
    fontWeight: 950,
    marginTop: 3,
  },
  mobileCardUpdated: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    margin: "13px 0",
    overflowWrap: "anywhere",
  },
  mobileListActions: {
    gridColumn: "1 / -1",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    paddingTop: 8,
  },
  previewPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    position: "sticky",
    top: 20,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 22,
    boxShadow: "0 24px 52px rgba(15,23,42,0.10)",
  },
  previewCollapsedPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    position: "sticky",
    top: 20,
    display: "flex",
    justifyContent: "center",
  },
  showPreviewButton: {
    width: "100%",
    minHeight: 48,
    border: "1px solid rgba(37,99,235,0.30)",
    borderRadius: 16,
    background: "#FFFFFF",
    color: "#2563EB",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(15,23,42,0.08)",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(148,163,184,0.20)",
    paddingBottom: 14,
    marginBottom: 16,
  },
  previewTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  previewChevron: {
    color: "#0F172A",
    fontWeight: 950,
  },
  collapsePreviewButton: {
    border: "1px solid rgba(148,163,184,0.26)",
    background: "#FFFFFF",
    color: "#334155",
    borderRadius: 10,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  previewContractTitleRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  previewTitleTextWrap: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  previewContractTitle: {
    maxWidth: "100%",
    overflowWrap: "anywhere",
    margin: 0,
    fontSize: 16,
    color: "#0F172A",
    fontWeight: 950,
    lineHeight: 1.25,
  },
  previewJobId: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 800,
    marginTop: 6,
  },
  previewUpdated: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 12,
  },
  previewDivider: {
    height: 1,
    background: "rgba(148,163,184,0.20)",
    margin: "16px 0",
  },
  previewSectionTitle: {
    margin: "14px 0 8px",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  previewSummary: {
    maxWidth: "100%",
    overflowWrap: "anywhere",
    margin: 0,
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.65,
    fontWeight: 650,
  },
  previewScoreGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16,
  },
  previewScoreCard: {
    minWidth: 0,
    minHeight: 92,
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 16,
    padding: "13px 10px",
    background: "#FFFFFF",
    textAlign: "center",
    display: "grid",
    alignContent: "center",
    gap: 8,
    boxShadow: "0 10px 24px rgba(15,23,42,0.045)",
  },
  previewScoreTitle: {
    minHeight: 28,
    color: "#0F172A",
    fontSize: 11.5,
    fontWeight: 950,
    lineHeight: 1.25,
    display: "grid",
    placeItems: "center",
    overflowWrap: "anywhere",
  },
  previewCoverageValue: {
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  previewGaugeWrap: {
    minHeight: 72,
    display: "grid",
    placeItems: "center",
  },
  previewGauge: {
    width: 68,
    height: 68,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
  },
  previewGaugeInner: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
  },
  previewGaugeValue: {
    fontWeight: 950,
    fontSize: 18,
    lineHeight: 1,
  },
  previewScoreBarWrap: {
    minHeight: 72,
    display: "grid",
    alignContent: "center",
    gap: 9,
  },
  previewScoreBarTop: {
    display: "flex",
    justifyContent: "center",
    alignItems: "baseline",
    gap: 3,
  },
  previewScoreBig: {
    fontWeight: 950,
    fontSize: 26,
    lineHeight: 1,
  },
  previewScoreUnit: {
    color: "#64748B",
    fontWeight: 800,
    fontSize: 9,
  },
  previewScoreTrack: {
    height: 8,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  previewScoreFill: {
    height: "100%",
    borderRadius: 999,
  },
  previewScoreLabel: {
    color: "#64748B",
    fontWeight: 750,
    fontSize: 10.5,
    lineHeight: 1.25,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  previewLoadingState: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    color: "#475569",
    fontWeight: 850,
  },
  statusPreviewCard: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    textAlign: "center",
    border: "1px solid #BFDBFE",
    borderRadius: 18,
    background: "#EFF6FF",
    padding: 22,
    overflow: "hidden",
  },
  statusPreviewCardError: {
    borderColor: "#FECACA",
    background: "#FEF2F2",
  },
  statusPreviewIcon: {
    width: 48,
    height: 48,
    margin: "0 auto 12px",
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "#DBEAFE",
    color: "#1D4ED8",
    fontSize: 22,
    fontWeight: 950,
  },
  statusPreviewIconError: {
    background: "#FEE2E2",
    color: "#B91C1C",
  },
  statusPreviewTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },
  statusPreviewText: {
    margin: "10px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  openFullButtonError: {
    background: "linear-gradient(135deg, #DC2626, #B91C1C)",
  },
  listPreview: {
    display: "grid",
    gap: 8,
  },
  previewListRow: {
    display: "grid",
    gridTemplateColumns: "18px 1fr",
    gap: 8,
    color: "#334155",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.35,
  },
  previewListIcon: {
    fontWeight: 950,
  },
  openFullButton: {
    width: "100%",
    marginTop: 20,
    border: "none",
    borderRadius: 13,
    padding: "13px 14px",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(37,99,235,0.24)",
  },
  previewEmpty: {
    textAlign: "center",
    padding: "28px 10px",
    color: "#475569",
  },
  emptyState: {
    maxWidth: 720,
    margin: "80px auto",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 24,
    padding: 34,
    textAlign: "center",
    boxShadow: "0 22px 48px rgba(15,23,42,0.08)",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    margin: "0 auto 14px",
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "#EFF6FF",
    color: "#2563EB",
    fontSize: 26,
    fontWeight: 950,
  },
  emptyTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 28,
    fontWeight: 950,
  },
  emptyText: {
    margin: "10px auto 20px",
    color: "#475569",
    maxWidth: 540,
    lineHeight: 1.6,
  },
  emptyInline: {
    padding: "54px 20px",
    textAlign: "center",
  },
  emptyInlineTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
  },
  emptyInlineText: {
    margin: "8px auto 18px",
    color: "#64748B",
    maxWidth: 520,
    lineHeight: 1.6,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    padding: "12px 16px",
    background: "#2563EB",
    color: "#FFFFFF",
    textDecoration: "none",
    fontWeight: 950,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 5000,
    background: "rgba(15,23,42,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "min(520px, 100%)",
    background: "#FFFFFF",
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.24)",
    boxShadow: "0 30px 80px rgba(15,23,42,0.28)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    padding: "22px 24px 16px",
    borderBottom: "1px solid rgba(148,163,184,0.18)",
  },
  modalTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 20,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  modalSubtitle: {
    margin: "7px 0 0",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 650,
  },
  modalCloseButton: {
    border: "none",
    background: "#F1F5F9",
    color: "#334155",
    width: 34,
    height: 34,
    borderRadius: 999,
    fontSize: 22,
    lineHeight: "30px",
    cursor: "pointer",
    fontWeight: 800,
  },
  modalBody: {
    padding: "18px 24px 8px",
  },
  modalItemName: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 12,
  },
  modalInput: {
    width: "100%",
    minHeight: 46,
    border: "1px solid rgba(148,163,184,0.34)",
    borderRadius: 12,
    padding: "0 14px",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "18px 24px 22px",
  },
  modalCancelButton: {
    border: "1px solid rgba(148,163,184,0.34)",
    background: "#FFFFFF",
    color: "#334155",
    borderRadius: 12,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  modalConfirmButton: {
    border: "none",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    borderRadius: 12,
    padding: "10px 16px",
    minWidth: 112,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(37,99,235,0.20)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  modalDangerButton: {
    background: "linear-gradient(135deg, #DC2626, #B91C1C)",
    boxShadow: "0 12px 24px rgba(220,38,38,0.20)",
  },
  modalSubmittingButton: {
    cursor: "wait",
    opacity: 0.88,
    boxShadow: "none",
  },
  modalDisabledButton: {
    cursor: "not-allowed",
    opacity: 0.58,
  },

  errorBox: {
    maxWidth: "100%",
    margin: "0 auto 16px",
    background: "#FEF2F2",
    color: "#991B1B",
    border: "1px solid #FECACA",
    borderRadius: 14,
    padding: "12px 14px",
    fontWeight: 750,
  },
  loadingCard: {
    maxWidth: 520,
    margin: "90px auto",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 22,
    padding: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
  },
  loadingTitle: {
    fontWeight: 900,
    color: "#0F172A",
  },
  loadingRows: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    color: "#475569",
    fontWeight: 850,
  },
};
